import { useEffect, useRef, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ShieldCheck, AlertTriangle, Upload, Sparkles, Trash2 } from "lucide-react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";

export default function MeCompliance() {
  const [certs, setCerts] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/me/certifications").then(r => setCerts(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await fetch(`${API}/me/certifications`, { method: "POST", body: fd, credentials: "include" }).then(r => r.json().then(j => ({ data: j, ok: r.ok })));
      toast.success("Certification uploaded — AI extracting details");
      load();
    } catch (err) { toast.error("Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const remove = async (id) => {
    try { await api.delete(`/me/certifications/${id}`); toast.success("Removed"); load(); }
    catch { toast.error("Failed"); }
  };

  const isExpiring = (dateStr) => {
    if (!dateStr) return false;
    try {
      const d = new Date(dateStr);
      const days = (d - new Date()) / (1000 * 60 * 60 * 24);
      return days < 30 && days > 0;
    } catch { return false; }
  };
  const isExpired = (dateStr) => {
    if (!dateStr) return false;
    try { return new Date(dateStr) < new Date(); } catch { return false; }
  };

  return (
    <div className="space-y-6" data-testid="me-compliance">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-neutral-500">My Compliance</div>
          <h1 className="text-3xl font-bold mt-1">Certification wallet</h1>
          <p className="text-neutral-600 mt-1">Upload a licence photo — AI auto-extracts the licence number and expiry date.</p>
        </div>
        <input ref={fileRef} type="file" accept="image/*,application/pdf" hidden onChange={onFile} data-testid="cert-upload-input" />
        <Button className="btn-primary rounded-full" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-cert-btn">
          <Upload className="w-4 h-4 mr-1" /> {uploading ? "Uploading…" : "Upload certification"}
        </Button>
      </div>

      {certs.length === 0 && (
        <Card className="border-0 shadow-sm"><CardContent className="p-10 text-center text-neutral-500">
          No certifications on file. Tap "Upload certification" to add your first one — AI will extract details automatically.
        </CardContent></Card>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {certs.map(c => {
          const x = c.extracted || {};
          const status = isExpired(x.expiry_date) ? "Expired" : isExpiring(x.expiry_date) ? "Expiring" : "Active";
          const color = status === "Expired" ? "#E53E3E" : status === "Expiring" ? "#F6AD55" : "#0CDC73";
          return (
            <Card key={c.certification_id} className="border-0 shadow-sm" data-testid={`cert-${c.certification_id}`}>
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-lg grid place-items-center" style={{ backgroundColor: `${color}20` }}>
                    {status === "Active" ? <ShieldCheck className="w-5 h-5" style={{color}} /> : <AlertTriangle className="w-5 h-5" style={{color}} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-semibold truncate">{x.certificate_name || c.original_filename || "Certification"}</div>
                      <Badge style={{ backgroundColor: color, color: "white", border: 0 }}>{status}</Badge>
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                      {x.license_number && <div><div className="text-neutral-500">Licence #</div><div className="font-mono">{x.license_number}</div></div>}
                      {x.expiry_date && <div><div className="text-neutral-500">Expires</div><div className="font-medium">{x.expiry_date}</div></div>}
                      {x.issued_by && <div><div className="text-neutral-500">Issued by</div><div className="font-medium">{x.issued_by}</div></div>}
                      {x.issued_date && <div><div className="text-neutral-500">Issued</div><div className="font-medium">{x.issued_date}</div></div>}
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      <div className="text-[10px] uppercase text-neutral-500 tracking-wider flex items-center gap-1">
                        <Sparkles className="w-3 h-3 text-pink" /> AI extracted
                      </div>
                      <button onClick={() => remove(c.certification_id)} className="text-neutral-400 hover:text-red-500" data-testid={`cert-delete-${c.certification_id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
